// M7.3 — Zone catalogue. Promotes the programmatic `buildEldoria()` /
// `buildVerdoie()` / ... builders to a flat, editable data model so the
// admin Map Editor can author maps as JSON.
//
// Shape rules:
// - `terrain` is the raw width*height grid (TerrainId per cell). It's the
//   ONLY source of truth for tile painting. Collision is DERIVED from
//   terrain (grass + ice are walkable, everything else blocks). The admin
//   never edits collision directly.
// - `spawnPoints[i].index` should normally equal `i`, but we keep it
//   explicit so portals can reference an index that doesn't shuffle when
//   spawn points get reordered in the JSON.
// - Portals are placed ON TOP of the terrain layer; at runtime their tile
//   is forced to walkable wood (handled in the server's tilemap-helpers
//   build pass — keeps the wire format minimal).
// - `mobSpawns` / `npcSpawns` mirror the existing shapes from mobs.ts and
//   the server-side NpcSpawnDef. Identical semantics; the admin can
//   reorder freely.
//
// Runtime: same registry pattern as items/mobs/affixes/etc. — a
// `BUILTIN_ZONE_DEFINITIONS` const + a `setZoneCatalogue` mutator + a
// `getZoneTemplate(id)` lookup. Server iterates `getZoneCatalogue()` at
// boot via instance.ts.

import { z } from "zod";
import { TILE_H, TILE_W, type Facing } from "../types.js";
import {
  type Portal,
  type SpawnPoint,
  type TerrainId,
  type TilemapJson,
} from "../world/tilemap.js";
import type { MobSpawnDef } from "./mobs.js";

// Re-exported for callers building a ZoneJson programmatically (the
// builtin definitions below + the one-time conversion from the legacy
// tilemap-*.ts files use this).
export interface ZoneNpcSpawnDef {
  templateId: string;
  x: number;
  y: number;
  facing: Facing;
}

// M14 — decor placement. `kind` is a sprite registry key resolved
// client-side (see client/src/render/decor.ts). The engine does not
// validate `kind` against a backend catalogue yet (no decor authoring app
// at this MVP stage) — unknown kinds are dropped silently by the client.
// Position is in zone tile coordinates; the client anchors the sprite at
// (0.5, 1.0) so the base sits on tile (tx, ty).
export interface DecorPlacement {
  kind: string;
  tx: number;
  ty: number;
}

export interface ZoneJson {
  id: string;
  // Optional human-readable label for the admin list (e.g. "Eldoria —
  // starting town"). Falls back to `id` when missing.
  displayName?: string;
  width: number;
  height: number;
  isSafe: boolean;
  terrain: TerrainId[];
  spawnPoints: SpawnPoint[];
  portals: Portal[];
  mobSpawns: MobSpawnDef[];
  npcSpawns: ZoneNpcSpawnDef[];
  // M14 — optional for back-compat with all pre-decor zones; treat
  // undefined as empty.
  decor?: DecorPlacement[];
}

// Used by the legacy builders + the one-time conversion below.
export type ObstacleDirective =
  | { kind: "tile"; x: number; y: number; terrain: TerrainId }
  | { kind: "rect"; x: number; y: number; w: number; h: number; terrain: TerrainId }
  | { kind: "hline"; x: number; y: number; w: number; terrain: TerrainId }
  | { kind: "vline"; x: number; y: number; h: number; terrain: TerrainId };

// =============================================================================
// Terrain helpers — used by the BUILTIN materialization AND by the server's
// tilemap-helpers when converting a ZoneJson back into a TilemapJson.
// =============================================================================

const NON_BLOCKING: ReadonlySet<TerrainId> = new Set<TerrainId>(["grass", "ice", "path"]);

export function terrainBlocks(t: TerrainId): boolean {
  return !NON_BLOCKING.has(t);
}

function ix(width: number, x: number, y: number): number {
  return y * width + x;
}

// Materialize a fresh terrain grid from a base + border + obstacles spec.
// Used ONLY at module load to compute BUILTIN_ZONE_DEFINITIONS. The admin
// editor edits the materialized grid directly, never via obstacles.
function expandTerrain(
  width: number,
  height: number,
  base: TerrainId,
  borderTerrain: TerrainId,
  obstacles: ObstacleDirective[],
): TerrainId[] {
  const grid: TerrainId[] = new Array(width * height).fill(base);
  // Border ring.
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      if (x === 0 || y === 0 || x === width - 1 || y === height - 1) {
        grid[ix(width, x, y)] = borderTerrain;
      }
    }
  }
  // Obstacles.
  for (const ob of obstacles) {
    switch (ob.kind) {
      case "tile":
        paint(grid, width, height, ob.x, ob.y, ob.terrain);
        break;
      case "rect":
        for (let dx = 0; dx < ob.w; dx++) {
          for (let dy = 0; dy < ob.h; dy++) {
            paint(grid, width, height, ob.x + dx, ob.y + dy, ob.terrain);
          }
        }
        break;
      case "hline":
        for (let dx = 0; dx < ob.w; dx++) {
          paint(grid, width, height, ob.x + dx, ob.y, ob.terrain);
        }
        break;
      case "vline":
        for (let dy = 0; dy < ob.h; dy++) {
          paint(grid, width, height, ob.x, ob.y + dy, ob.terrain);
        }
        break;
    }
  }
  return grid;
}

function paint(
  grid: TerrainId[],
  width: number,
  height: number,
  x: number,
  y: number,
  t: TerrainId,
): void {
  if (x < 0 || y < 0 || x >= width || y >= height) return;
  grid[ix(width, x, y)] = t;
}

// Convert a flat ZoneJson into a fully-baked TilemapJson (collision
// derived, portals forced walkable). The server calls this at boot via
// tilemap-helpers.ts.
export function zoneToTilemap(zone: ZoneJson): TilemapJson {
  const collision: boolean[] = new Array(zone.width * zone.height);
  for (let i = 0; i < zone.terrain.length; i++) {
    collision[i] = terrainBlocks(zone.terrain[i]!);
  }
  // Portals always land on walkable tiles. We don't mutate `zone.terrain`
  // (keep the on-disk shape pristine) — the runtime tilemap gets a fresh
  // background array with portal cells painted as wood.
  const background: TerrainId[] = zone.terrain.slice();
  for (const p of zone.portals) {
    if (p.x < 0 || p.y < 0 || p.x >= zone.width || p.y >= zone.height) continue;
    const i = ix(zone.width, p.x, p.y);
    background[i] = "wood";
    collision[i] = false;
  }
  return {
    zoneId: zone.id,
    width: zone.width,
    height: zone.height,
    tileWidth: TILE_W,
    tileHeight: TILE_H,
    isSafe: zone.isSafe,
    background,
    collision,
    spawnPoints: zone.spawnPoints.slice(),
    portals: zone.portals.slice(),
  };
}

// =============================================================================
// BUILTIN zone definitions — byte-for-byte equivalent to the legacy
// tilemap-*.ts builders. Generated by feeding each builder's args through
// expandTerrain at module load. Fallback when no /content/zones/*.json
// files are present.
// =============================================================================

const ZONE_W = 32;
const ZONE_H = 32;

function buildBuiltin(args: {
  id: string;
  displayName: string;
  isSafe: boolean;
  base: TerrainId;
  borderTerrain: TerrainId;
  width?: number;
  height?: number;
  obstacles: ObstacleDirective[];
  spawnPoints: SpawnPoint[];
  portals: Portal[];
  mobSpawns: MobSpawnDef[];
  npcSpawns?: ZoneNpcSpawnDef[];
}): ZoneJson {
  const width = args.width ?? ZONE_W;
  const height = args.height ?? ZONE_H;
  return {
    id: args.id,
    displayName: args.displayName,
    width,
    height,
    isSafe: args.isSafe,
    terrain: expandTerrain(width, height, args.base, args.borderTerrain, args.obstacles),
    spawnPoints: args.spawnPoints,
    portals: args.portals,
    mobSpawns: args.mobSpawns,
    npcSpawns: args.npcSpawns ?? [],
  };
}

const ELDORIA: ZoneJson = buildBuiltin({
  id: "eldoria",
  displayName: "Eldoria — starting town",
  isSafe: true,
  base: "grass",
  borderTerrain: "stone",
  obstacles: [
    { kind: "hline", x: 10, y: 10, w: 4, terrain: "stone" },
    { kind: "vline", x: 22, y: 20, h: 3, terrain: "stone" },
  ],
  spawnPoints: [
    { x: 16, y: 16, index: 0 },
    { x: 16, y: 28, index: 1 },
    { x: 13, y: 16, index: 2 },
    { x: 11, y: 16, index: 3 },
  ],
  portals: [
    { x: 16, y: 30, targetZoneId: "verdoie", targetSpawnIndex: 0 },
    { x: 12, y: 16, targetZoneId: "arena", targetSpawnIndex: 0 },
    { x: 10, y: 16, targetZoneId: "frost_corridor", targetSpawnIndex: 0 },
  ],
  mobSpawns: [
    { templateId: "training_dummy", x: 20, y: 16 },
    { templateId: "training_dummy", x: 23, y: 16 },
  ],
  npcSpawns: [
    { templateId: "eldoria_general_vendor", x: 18, y: 16, facing: "S" },
    { templateId: "eldoria_captain", x: 14, y: 16, facing: "S" },
  ],
});

const VERDOIE: ZoneJson = buildBuiltin({
  id: "verdoie",
  displayName: "Verdoie — green fields",
  isSafe: false,
  base: "grass",
  borderTerrain: "stone",
  obstacles: [
    { kind: "rect", x: 6, y: 8, w: 2, h: 2, terrain: "wood" },
    { kind: "rect", x: 24, y: 7, w: 2, h: 2, terrain: "wood" },
    { kind: "rect", x: 12, y: 14, w: 2, h: 2, terrain: "wood" },
    { kind: "rect", x: 22, y: 18, w: 2, h: 2, terrain: "wood" },
    { kind: "rect", x: 8, y: 22, w: 2, h: 2, terrain: "wood" },
    { kind: "rect", x: 4, y: 4, w: 3, h: 3, terrain: "water" },
    { kind: "rect", x: 26, y: 24, w: 3, h: 3, terrain: "water" },
  ],
  spawnPoints: [
    { x: 16, y: 4, index: 0 },
    { x: 16, y: 27, index: 1 },
  ],
  portals: [
    { x: 16, y: 1, targetZoneId: "eldoria", targetSpawnIndex: 1 },
    { x: 16, y: 30, targetZoneId: "darkwood", targetSpawnIndex: 0 },
  ],
  mobSpawns: [
    { templateId: "cave_rat", x: 8, y: 12 },
    { templateId: "cave_rat", x: 14, y: 8 },
    { templateId: "cave_rat", x: 20, y: 12 },
    { templateId: "cave_rat", x: 26, y: 14 },
    { templateId: "cave_rat", x: 10, y: 18 },
    { templateId: "cave_rat", x: 24, y: 21 },
    { templateId: "grey_wolf", x: 20, y: 24 },
    { templateId: "grey_wolf", x: 12, y: 25 },
    { templateId: "grey_wolf", x: 6, y: 16 },
    { templateId: "grey_wolf", x: 28, y: 9 },
  ],
});

const DARKWOOD: ZoneJson = buildBuiltin({
  id: "darkwood",
  displayName: "Darkwood — goblin warrens",
  isSafe: false,
  base: "grass",
  borderTerrain: "wood",
  obstacles: [
    { kind: "hline", x: 4, y: 6, w: 10, terrain: "wood" },
    { kind: "hline", x: 18, y: 6, w: 10, terrain: "wood" },
    { kind: "vline", x: 8, y: 10, h: 6, terrain: "wood" },
    { kind: "vline", x: 24, y: 10, h: 6, terrain: "wood" },
    { kind: "hline", x: 10, y: 14, w: 12, terrain: "wood" },
    { kind: "vline", x: 6, y: 18, h: 8, terrain: "wood" },
    { kind: "vline", x: 26, y: 18, h: 8, terrain: "wood" },
    { kind: "hline", x: 8, y: 22, w: 6, terrain: "wood" },
    { kind: "hline", x: 18, y: 22, w: 6, terrain: "wood" },
    { kind: "rect", x: 14, y: 18, w: 4, h: 2, terrain: "wood" },
  ],
  spawnPoints: [
    { x: 16, y: 4, index: 0 },
    { x: 16, y: 27, index: 1 },
  ],
  portals: [
    { x: 16, y: 1, targetZoneId: "verdoie", targetSpawnIndex: 1 },
    { x: 16, y: 30, targetZoneId: "icecaves", targetSpawnIndex: 0 },
  ],
  mobSpawns: [
    { templateId: "goblin_warrior", x: 10, y: 9 },
    { templateId: "goblin_warrior", x: 22, y: 9 },
    { templateId: "goblin_warrior", x: 12, y: 18 },
    { templateId: "goblin_warrior", x: 20, y: 18 },
    { templateId: "goblin_warrior", x: 16, y: 25 },
    { templateId: "skeleton_mage", x: 10, y: 25 },
    { templateId: "skeleton_mage", x: 22, y: 25 },
    { templateId: "skeleton_mage", x: 16, y: 12 },
  ],
});

const ICECAVES: ZoneJson = buildBuiltin({
  id: "icecaves",
  displayName: "Ice Caves",
  isSafe: false,
  base: "ice",
  borderTerrain: "stone",
  obstacles: [
    { kind: "rect", x: 6, y: 8, w: 8, h: 1, terrain: "stone" },
    { kind: "rect", x: 18, y: 8, w: 8, h: 1, terrain: "stone" },
    { kind: "rect", x: 6, y: 14, w: 1, h: 8, terrain: "stone" },
    { kind: "rect", x: 25, y: 14, w: 1, h: 8, terrain: "stone" },
    { kind: "rect", x: 10, y: 18, w: 4, h: 1, terrain: "stone" },
    { kind: "rect", x: 18, y: 18, w: 4, h: 1, terrain: "stone" },
    { kind: "rect", x: 14, y: 22, w: 4, h: 2, terrain: "water" },
  ],
  spawnPoints: [{ x: 16, y: 4, index: 0 }],
  portals: [{ x: 16, y: 1, targetZoneId: "darkwood", targetSpawnIndex: 1 }],
  mobSpawns: [
    { templateId: "brown_bear", x: 10, y: 12 },
    { templateId: "brown_bear", x: 22, y: 12 },
    { templateId: "goblin_warrior", x: 12, y: 20 },
    { templateId: "goblin_warrior", x: 20, y: 20 },
    { templateId: "skeleton_mage", x: 10, y: 26 },
    { templateId: "skeleton_mage", x: 22, y: 26 },
  ],
});

const ARENA: ZoneJson = buildBuiltin({
  id: "arena",
  displayName: "Test Arena (projectile sandbox)",
  isSafe: false,
  base: "grass",
  borderTerrain: "stone",
  obstacles: [
    { kind: "rect", x: 18, y: 18, w: 4, h: 4, terrain: "stone" },
  ],
  spawnPoints: [{ x: 16, y: 24, index: 0 }],
  portals: [{ x: 16, y: 30, targetZoneId: "eldoria", targetSpawnIndex: 2 }],
  mobSpawns: [{ templateId: "skeleton_mage", x: 16, y: 16 }],
});

const FROST_CORRIDOR: ZoneJson = (() => {
  const corridorTop = 15;
  const corridorBottom = 16;
  return buildBuiltin({
    id: "frost_corridor",
    displayName: "Frost Corridor (slow-test corridor)",
    isSafe: false,
    base: "grass",
    borderTerrain: "stone",
    obstacles: [
      { kind: "rect", x: 1, y: 1, w: ZONE_W - 2, h: corridorTop - 1, terrain: "stone" },
      {
        kind: "rect",
        x: 1,
        y: corridorBottom + 1,
        w: ZONE_W - 2,
        h: ZONE_H - corridorBottom - 2,
        terrain: "stone",
      },
    ],
    spawnPoints: [{ x: 3, y: 16, index: 0 }],
    portals: [{ x: 1, y: 16, targetZoneId: "eldoria", targetSpawnIndex: 3 }],
    mobSpawns: [{ templateId: "frost_test_walker", x: ZONE_W - 4, y: 15 }],
  });
})();

export const BUILTIN_ZONE_DEFINITIONS: readonly ZoneJson[] = [
  ELDORIA,
  VERDOIE,
  DARKWOOD,
  ICECAVES,
  ARENA,
  FROST_CORRIDOR,
];

// =============================================================================
// Runtime registry (M7.3) — mirrors the items / mobs / npcs pattern.
// =============================================================================

const runtimeZoneRegistry = new Map<string, ZoneJson>(
  BUILTIN_ZONE_DEFINITIONS.map((z) => [z.id, z] as const),
);

export function getZoneTemplate(id: string): ZoneJson | undefined {
  return runtimeZoneRegistry.get(id);
}

export function getZoneCatalogue(): ZoneJson[] {
  return Array.from(runtimeZoneRegistry.values());
}

export function setZoneCatalogue(zones: readonly ZoneJson[]): void {
  runtimeZoneRegistry.clear();
  for (const z of zones) runtimeZoneRegistry.set(z.id, z);
}

export function _resetZoneCatalogueForTests(): void {
  runtimeZoneRegistry.clear();
  for (const z of BUILTIN_ZONE_DEFINITIONS) {
    runtimeZoneRegistry.set(z.id, z);
  }
}

// =============================================================================
// Zod schemas (M7.3) — used by admin publish, disk loader, and protocol.
// =============================================================================

// M11 raised the ceiling from 64 to 1024 once chunked streaming was
// in place — terrain no longer ships as one snapshot, the server
// pushes 32×32 chunks based on viewport, and the pathfinder is
// node-budgeted. Memory cost is the only remaining floor: a 1024²
// zone holds 1 M cells × ~1 byte effective = ~1 MB per zone.
const ZONE_MIN_SIZE = 8;
const ZONE_MAX_SIZE = 1024;

const ZONE_ID_REGEX = /^[a-z][a-z0-9_]{1,40}$/;

const TerrainSchema = z.enum(["grass", "stone", "wood", "ice", "water", "path"]);
const FacingSchema = z.enum(["N", "S", "E", "W"]);

const SpawnPointSchema = z
  .object({
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    index: z.number().int().min(0).max(31),
  })
  .strict();

const PortalSchema = z
  .object({
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    targetZoneId: z.string().regex(ZONE_ID_REGEX),
    targetSpawnIndex: z.number().int().min(0).max(31),
  })
  .strict();

const MobSpawnSchema = z
  .object({
    templateId: z.string().min(1),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
  })
  .strict();

const NpcSpawnSchema = z
  .object({
    templateId: z.string().min(1),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    facing: FacingSchema,
  })
  .strict();

// M14 — kind is a free-form sprite registry key (client-resolved). Bounded
// to keep JSON tidy; tx/ty bounds validated below against zone dims.
const DecorPlacementSchema = z
  .object({
    kind: z.string().min(1).max(64),
    tx: z.number().int().min(0),
    ty: z.number().int().min(0),
  })
  .strict();

export const ZoneJsonSchema = z
  .object({
    id: z.string().regex(ZONE_ID_REGEX),
    displayName: z.string().min(1).max(80).optional(),
    width: z.number().int().min(ZONE_MIN_SIZE).max(ZONE_MAX_SIZE),
    height: z.number().int().min(ZONE_MIN_SIZE).max(ZONE_MAX_SIZE),
    isSafe: z.boolean(),
    terrain: z.array(TerrainSchema),
    spawnPoints: z.array(SpawnPointSchema).min(1).max(16),
    portals: z.array(PortalSchema).max(32),
    mobSpawns: z.array(MobSpawnSchema).max(200),
    npcSpawns: z.array(NpcSpawnSchema).max(64),
    // Cap kept generous — a 1024² zone with one decor every 10 tiles is
    // ~10k entries. 20k gives headroom for hand-placement spurts.
    decor: z.array(DecorPlacementSchema).max(20000).optional(),
  })
  .strict()
  .refine((z) => z.terrain.length === z.width * z.height, {
    message: "terrain.length must equal width * height",
    path: ["terrain"],
  })
  .refine(
    (z) => z.spawnPoints.every((s) => s.x < z.width && s.y < z.height),
    { message: "spawnPoint outside zone bounds", path: ["spawnPoints"] },
  )
  .refine(
    (z) => z.portals.every((p) => p.x < z.width && p.y < z.height),
    { message: "portal outside zone bounds", path: ["portals"] },
  )
  .refine(
    (z) => z.mobSpawns.every((m) => m.x < z.width && m.y < z.height),
    { message: "mobSpawn outside zone bounds", path: ["mobSpawns"] },
  )
  .refine(
    (z) => z.npcSpawns.every((n) => n.x < z.width && n.y < z.height),
    { message: "npcSpawn outside zone bounds", path: ["npcSpawns"] },
  )
  .refine(
    (z) => (z.decor ?? []).every((d) => d.tx < z.width && d.ty < z.height),
    { message: "decor outside zone bounds", path: ["decor"] },
  )
  .refine(
    (z) => {
      const seen = new Set<number>();
      for (const s of z.spawnPoints) {
        if (seen.has(s.index)) return false;
        seen.add(s.index);
      }
      return true;
    },
    { message: "duplicate spawnPoint index", path: ["spawnPoints"] },
  );

export const ZoneCatalogueSchema = z.array(ZoneJsonSchema).min(1);

export type ZoneId = string;
